// types
import { IPlanItem } from 'types/graphql'

export type TreeUpdateItem = { id: string; parentId: string | null; expanded: boolean }
export type PositionedItem = TreeUpdateItem & { treePosition: number }

type WithParent = { id?: string | null; parentId?: string | null }

const ROOT_PARENT_ID = null

export const isArchived = (item: any) =>
  !!(item?.planObjective?.archived || item?.planAction?.archived || item?.planKPI?.archived)

const toTreeUpdateItem = (item: any): TreeUpdateItem => ({
  id: item.id,
  parentId: item.parentId ?? ROOT_PARENT_ID,
  expanded: !!item.expanded
})

/** Groups items by their parent id; root-level items are keyed under `null`. */
const groupByParent = <T extends WithParent>(items: T[]): Map<string | null, T[]> => {
  const groups = new Map<string | null, T[]>()

  for (const item of items) {
    const parentId = item.parentId ?? ROOT_PARENT_ID
    const siblings = groups.get(parentId) ?? []

    siblings.push(item)
    groups.set(parentId, siblings)
  }

  return groups
}

/** Maps each sibling id to its original rank (0-based index ordered by `treePosition`). */
const buildRankMap = (siblings: IPlanItem[]): Map<string, number> => {
  const sortedByPosition = [...siblings].sort((a, b) => a.treePosition - b.treePosition)
  const rankById = new Map<string, number>()

  sortedByPosition.forEach((item, index) => rankById.set(item.id as string, index))

  return rankById
}

/**
 * Merges reordered visible siblings with hidden archived siblings.
 * Visible items keep their new order, while each archived item is re-inserted
 * at its original position relative to the visible items.
 */
const mergeSiblings = (
  visibleSiblings: any[],
  archivedSiblings: IPlanItem[],
  rankOf: (id: string) => number
): TreeUpdateItem[] => {
  const archivedByRank = [...archivedSiblings].sort((a, b) => rankOf(a.id) - rankOf(b.id))
  const merged: TreeUpdateItem[] = []
  let nextArchived = 0

  const takeArchivedBefore = (rank: number) => {
    while (nextArchived < archivedByRank.length && rankOf(archivedByRank[nextArchived].id) < rank) {
      merged.push(toTreeUpdateItem(archivedByRank[nextArchived++]))
    }
  }

  for (const visibleItem of visibleSiblings) {
    takeArchivedBefore(rankOf(visibleItem.id))
    merged.push(toTreeUpdateItem(visibleItem))
  }

  // archived items ranked after every visible item go to the end
  while (nextArchived < archivedByRank.length) {
    merged.push(toTreeUpdateItem(archivedByRank[nextArchived++]))
  }

  return merged
}

/** Builds the final ordered sibling list for every parent, re-inserting archived items. */
const buildSiblingsByParent = (
  visibleItems: any[],
  archivedItems: IPlanItem[],
  allItems: IPlanItem[]
): Map<string | null, TreeUpdateItem[]> => {
  const visibleByParent = groupByParent(visibleItems)
  const archivedByParent = groupByParent(archivedItems)
  const allByParent = groupByParent(allItems)

  const parentIds = new Set<string | null>([...visibleByParent.keys(), ...archivedByParent.keys()])
  const siblingsByParent = new Map<string | null, TreeUpdateItem[]>()

  for (const parentId of parentIds) {
    const visibleSiblings = visibleByParent.get(parentId) ?? []
    const archivedSiblings = archivedByParent.get(parentId) ?? []

    if (archivedSiblings.length === 0) {
      siblingsByParent.set(parentId, visibleSiblings.map(toTreeUpdateItem))
      continue
    }

    const rankById = buildRankMap(allByParent.get(parentId) ?? [])
    const rankOf = (id: string) => rankById.get(id) ?? -1

    siblingsByParent.set(parentId, mergeSiblings(visibleSiblings, archivedSiblings, rankOf))
  }

  return siblingsByParent
}

/** Walks the tree depth-first, assigning a sequential `treePosition` to every item. */
const flattenToPositions = (siblingsByParent: Map<string | null, TreeUpdateItem[]>): PositionedItem[] => {
  const positioned: PositionedItem[] = []

  const childrenOf = (parentId: string | null) => siblingsByParent.get(parentId) ?? []

  // depth-first, pre-order: reverse so the stack pops siblings left-to-right
  const stack: TreeUpdateItem[] = [...childrenOf(ROOT_PARENT_ID)].reverse()

  while (stack.length > 0) {
    const item = stack.pop()

    positioned.push({ ...item, treePosition: positioned.length })
    stack.push(...[...childrenOf(item.id)].reverse())
  }

  return positioned
}

/**
 * Recomputes tree positions for all plan items when archived items are hidden.
 * Takes the reordered visible items, re-inserts the hidden archived items at
 * their original positions, and returns the full flattened list with positions.
 */
export const buildPositionsWithArchived = (visibleItems: any[], allItems: IPlanItem[]): PositionedItem[] => {
  const visibleIds = new Set(visibleItems.map((item) => item.id))
  const hiddenArchivedItems = allItems.filter((item) => isArchived(item) && !visibleIds.has(item.id))

  const siblingsByParent = buildSiblingsByParent(visibleItems, hiddenArchivedItems, allItems)

  return flattenToPositions(siblingsByParent)
}
